import type { ImageModel, LlmModel } from '@/shared/catalog/models';
import { getImageModel, getLlmModel } from '@/shared/catalog/models';
import type { Prediction } from '@/entities/prediction';

export interface CostRange {
  low: number;
  expected: number;
  high: number;
}

export interface JobEstimateInput {
  llmModelId: string;
  imageModelId: string;
  templateCount: number;
  numImagesPerTemplate: number;
  /** Avg input/output tokens we expect per LLM call across phases 1+2. */
  avgLlmInputTokens?: number;
  avgLlmOutputTokens?: number;
  llmCallsCount?: number;
}

const DEFAULT_AVG_INPUT_TOKENS = 1500;
const DEFAULT_AVG_OUTPUT_TOKENS = 1200;

/**
 * Pure cost estimator. Returns a {low, expected, high} range so we can
 * communicate uncertainty up-front. Hardware overruns and retries push
 * "high" up by 50% above expected.
 */
export function estimateJobCost(input: JobEstimateInput): CostRange {
  const llm = getLlmModel(input.llmModelId);
  const img = getImageModel(input.imageModelId);
  if (!llm) throw new Error(`Unknown LLM model: ${input.llmModelId}`);
  if (!img) throw new Error(`Unknown image model: ${input.imageModelId}`);

  // Brand DNA = 1 call. One prompt-fill call per template = N calls.
  const llmCalls = input.llmCallsCount ?? 1 + input.templateCount;
  const inTokens = input.avgLlmInputTokens ?? DEFAULT_AVG_INPUT_TOKENS;
  const outTokens = input.avgLlmOutputTokens ?? DEFAULT_AVG_OUTPUT_TOKENS;
  const llmCost = llmCallCost(llm, llmCalls, inTokens, outTokens);

  const totalImages = input.templateCount * input.numImagesPerTemplate;
  const imgCost = imageCost(img, totalImages);

  const expected = llmCost + imgCost;
  return {
    low: round(expected * 0.7),
    expected: round(expected),
    high: round(expected * 1.5),
  };
}

function llmCallCost(
  llm: LlmModel,
  calls: number,
  avgInputTokens: number,
  avgOutputTokens: number,
): number {
  const inUsd = (avgInputTokens / 1_000_000) * llm.pricing.inputUsdPerMillion;
  const outUsd = (avgOutputTokens / 1_000_000) * llm.pricing.outputUsdPerMillion;
  return (inUsd + outUsd) * calls;
}

function imageCost(img: ImageModel, totalImages: number): number {
  if (img.pricing.type === 'per-run') return img.pricing.usd * totalImages;
  return img.pricing.usdPerSecond * img.pricing.expectedSeconds * totalImages;
}

export function predictionActualCost(
  prediction: Pick<Prediction, 'modelSlug' | 'kind' | 'predictTimeSec' | 'inputTokens' | 'outputTokens'>,
): number {
  if (prediction.kind === 'llm') {
    const llm = getLlmModel(prediction.modelSlug);
    if (!llm) return 0;
    const inUsd = ((prediction.inputTokens ?? 0) / 1_000_000) * llm.pricing.inputUsdPerMillion;
    const outUsd = ((prediction.outputTokens ?? 0) / 1_000_000) * llm.pricing.outputUsdPerMillion;
    return round(inUsd + outUsd);
  }
  const img = getImageModel(prediction.modelSlug);
  if (!img) return 0;
  if (img.pricing.type === 'per-run') return round(img.pricing.usd);
  return round(img.pricing.usdPerSecond * (prediction.predictTimeSec ?? img.pricing.expectedSeconds));
}

export function actualizeJobCost(predictions: readonly Prediction[]): number {
  return round(predictions.reduce((sum, p) => sum + predictionActualCost(p), 0));
}

function round(usd: number): number {
  return Math.round(usd * 10000) / 10000;
}

export function formatUsd(usd: number): string {
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  if (usd < 1) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
}
